import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Alert,
  LinearProgress, TextField, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, Tooltip,
} from '@mui/material';
import {
  ExpandMore, FactCheck, PhoneAndroid, Storage, Web,
  AddCircleOutline, DeleteOutline,
} from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import { SectionHeader } from './DocPrimitives';
import {
  AUTH_TESTS, RIDE_LIFECYCLE_TESTS, CASH_TESTS, PAYMENT_TESTS,
  KYC_TESTS, REALTIME_TESTS, SAFETY_TESTS, ADMIN_TESTS,
  BACKEND_TESTS, DEVICE_TESTS,
  type TestItem, type TestStatus,
} from '../../data/docs/testing';
import {
  LIST_TEST_RUNS, CREATE_TEST_RUN,
  UPDATE_TEST_RUN_RESULT, DELETE_TEST_RUN,
} from '../../graphql/queries';
import { useAuth } from '../../contexts/AuthContext';

interface TestRun {
  runId: string; label: string; createdAt: string;
  updatedAt: string; createdBy: string; results: string;
}

function parseResults(raw: string): Record<string, TestStatus> {
  try { return JSON.parse(raw) ?? {}; } catch { return {}; }
}

function ScopeChip({ scope }: { scope: TestItem['scope'] }) {
  const map: Record<string, { label: string; color: 'default' | 'primary' | 'secondary' | 'info' | 'warning' }> = {
    backend: { label: 'Backend', color: 'secondary' },
    mobile:  { label: 'Mobile',  color: 'primary' },
    portal:  { label: 'Portal',  color: 'info' },
    all:     { label: 'All',     color: 'warning' },
  };
  const { label, color } = map[scope] ?? { label: scope, color: 'default' };
  return <Chip label={label} size="small" color={color} variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />;
}

function StatusChip({ status, onClick }: { status: TestStatus; onClick: () => void }) {
  const map: Record<TestStatus, { label: string; color: 'default' | 'success' | 'error' | 'warning' }> = {
    pass: { label: '✓ Pass', color: 'success' },
    fail: { label: '✗ Fail', color: 'error' },
    skip: { label: '— Skip', color: 'warning' },
  };
  const { label, color } = map[status];
  return <Chip label={label} size="small" color={color} onClick={onClick} sx={{ cursor: 'pointer', minWidth: 72, fontSize: '0.72rem' }} />;
}

function PendingChip({ onClick }: { onClick: () => void }) {
  return <Chip label="Pending" size="small" variant="outlined" onClick={onClick} sx={{ cursor: 'pointer', minWidth: 72, fontSize: '0.72rem', color: 'text.secondary' }} />;
}

function TestSection({ title, tests, results, onToggle }: {
  title: string; tests: TestItem[];
  results: Record<string, TestStatus>; onToggle: (id: string) => void;
}) {
  const passed = tests.filter(t => results[t.id] === 'pass').length;
  const failed = tests.filter(t => results[t.id] === 'fail').length;
  const total  = tests.length;
  const pct    = Math.round((passed / total) * 100);
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <Typography fontWeight={600} sx={{ flex: 1 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{passed}/{total}</Typography>
          {passed === total && <Chip label="All passed" size="small" color="success" />}
          {failed > 0 && <Chip label={`${failed} failed`} size="small" color="error" />}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <LinearProgress variant="determinate" value={pct}
          color={failed > 0 ? 'error' : passed === total ? 'success' : 'primary'} sx={{ height: 3 }} />
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell width="5%"><Typography variant="caption" color="text.secondary">ID</Typography></TableCell>
              <TableCell><Typography variant="caption" color="text.secondary">Test</Typography></TableCell>
              <TableCell width="8%"><Typography variant="caption" color="text.secondary">Scope</Typography></TableCell>
              <TableCell width="10%" align="center"><Typography variant="caption" color="text.secondary">Result</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tests.map(t => (
              <TableRow key={t.id} sx={{
                bgcolor: results[t.id] === 'fail' ? 'rgba(239,68,68,0.04)'
                       : results[t.id] === 'pass' ? 'rgba(34,197,94,0.03)' : 'inherit',
              }}>
                <TableCell><Typography variant="caption" color="text.disabled" fontFamily="monospace">{t.id}</Typography></TableCell>
                <TableCell><Typography variant="body2">{t.test}</Typography></TableCell>
                <TableCell><ScopeChip scope={t.scope} /></TableCell>
                <TableCell align="center">
                  {results[t.id]
                    ? <StatusChip status={results[t.id]} onClick={() => onToggle(t.id)} />
                    : <PendingChip onClick={() => onToggle(t.id)} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
}

const ALL_SECTIONS = [
  { title: '1. Authentication & Access Control',  tests: AUTH_TESTS },
  { title: '2. Full Ride Lifecycle',              tests: RIDE_LIFECYCLE_TESTS },
  { title: '3. Cash Payment Flow',                tests: CASH_TESTS },
  { title: '4. Payments — Mobile Money & Card',   tests: PAYMENT_TESTS },
  { title: '5. KYC & Driver Onboarding',          tests: KYC_TESTS },
  { title: '6. Real-Time & Connectivity',         tests: REALTIME_TESTS },
  { title: '7. SOS & Safety',                     tests: SAFETY_TESTS },
  { title: '8. Admin Portal Operations',          tests: ADMIN_TESTS },
  { title: '9. Backend Infrastructure',           tests: BACKEND_TESTS },
  { title: '10. Device & Platform',               tests: DEVICE_TESTS },
];

const ALL_TESTS = ALL_SECTIONS.flatMap(s => s.tests);
const CYCLE_ORDER: TestStatus[] = ['pass', 'fail', 'skip'];

export default function TestingTab() {
  const { user } = useAuth();
  const username = user?.username ?? 'unknown';
  const client   = generateClient();

  const [runs, setRuns]               = useState<TestRun[]>([]);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [showNewRun, setShowNewRun]   = useState(false);
  const [newLabel, setNewLabel]       = useState('');
  const [creating, setCreating]       = useState(false);

  const activeRun = runs.find(r => r.runId === activeRunId) ?? null;
  const results   = activeRun ? parseResults(activeRun.results) : {};

  const loadRuns = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res: any = await client.graphql({ query: LIST_TEST_RUNS });
      const fetched: TestRun[] = res.data.listTestRuns ?? [];
      setRuns(fetched);
      if (fetched.length > 0 && !activeRunId) setActiveRunId(fetched[0].runId);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load test runs');
    } finally { setLoading(false); }
  }, [activeRunId]);

  useEffect(() => { loadRuns(); }, []);

  const createRun = async () => {
    if (!newLabel.trim()) return;
    const runId = `${newLabel.trim().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}`;
    setCreating(true);
    try {
      const res: any = await client.graphql({
        query: CREATE_TEST_RUN,
        variables: { input: { runId, label: newLabel.trim(), createdBy: username } },
      });
      const created: TestRun = res.data.createTestRun;
      setRuns(prev => [created, ...prev]);
      setActiveRunId(created.runId);
      setNewLabel(''); setShowNewRun(false);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to create test run');
    } finally { setCreating(false); }
  };

  const toggle = async (testId: string) => {
    if (!activeRunId) return;
    const current = results[testId];
    let next: string;
    if (!current) { next = 'pass'; }
    else {
      const idx = CYCLE_ORDER.indexOf(current as TestStatus);
      const s   = CYCLE_ORDER[(idx + 1) % CYCLE_ORDER.length];
      next = s === 'pass' ? 'pending' : s;
    }
    // Optimistic update
    const optimistic = next === 'pending'
      ? (() => { const r = { ...results }; delete r[testId]; return r; })()
      : { ...results, [testId]: next as TestStatus };
    setRuns(prev => prev.map(r =>
      r.runId === activeRunId ? { ...r, results: JSON.stringify(optimistic), updatedAt: new Date().toISOString() } : r
    ));
    setSaving(true);
    try {
      const res: any = await client.graphql({
        query: UPDATE_TEST_RUN_RESULT,
        variables: { input: { runId: activeRunId, testId, status: next } },
      });
      const updated: TestRun = res.data.updateTestRunResult;
      setRuns(prev => prev.map(r => r.runId === activeRunId ? { ...r, ...updated } : r));
    } catch (e: any) {
      setRuns(prev => prev.map(r => r.runId === activeRunId ? { ...r, results: JSON.stringify(results) } : r));
      setError('Failed to save result — please try again');
    } finally { setSaving(false); }
  };

  const deleteRun = async (runId: string) => {
    try {
      await client.graphql({ query: DELETE_TEST_RUN, variables: { runId } });
      const updated = runs.filter(r => r.runId !== runId);
      setRuns(updated);
      if (activeRunId === runId) setActiveRunId(updated[0]?.runId ?? null);
    } catch (e: any) { setError(e?.message ?? 'Failed to delete run'); }
  };

  const totalPassed  = ALL_TESTS.filter(t => results[t.id] === 'pass').length;
  const totalFailed  = ALL_TESTS.filter(t => results[t.id] === 'fail').length;
  const totalPending = ALL_TESTS.filter(t => !results[t.id]).length;
  const total        = ALL_TESTS.length;
  const pct          = Math.round((totalPassed / total) * 100);
  const readyForProd = totalFailed === 0 && totalPending === 0;

  return (
    <Box>
      <SectionHeader icon={<FactCheck />} title="Pre-Production Test Checklist"
        subtitle="Non-negotiable. Every item must pass before any change is promoted to UAT or Prod — backend, mobile, or portal." />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Run selector */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="subtitle2" color="text.secondary" sx={{ minWidth: 80 }}>Test run:</Typography>
          {loading ? <CircularProgress size={18} /> : runs.length === 0 ? (
            <Typography variant="body2" color="text.disabled">No runs yet — create one to start</Typography>
          ) : (
            <Box display="flex" gap={1} flexWrap="wrap">
              {runs.map(r => (
                <Chip key={r.runId} label={r.label} size="small"
                  variant={r.runId === activeRunId ? 'filled' : 'outlined'}
                  color={r.runId === activeRunId ? 'primary' : 'default'}
                  onClick={() => setActiveRunId(r.runId)}
                  onDelete={() => deleteRun(r.runId)}
                  deleteIcon={<DeleteOutline fontSize="small" />}
                />
              ))}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          {saving && <CircularProgress size={16} />}
          <Tooltip title="New test run">
            <Button size="small" startIcon={<AddCircleOutline />} onClick={() => setShowNewRun(true)}>New run</Button>
          </Tooltip>
        </Box>
        {activeRun && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Created by {activeRun.createdBy} on {new Date(activeRun.createdAt).toLocaleString()}
            {activeRun.updatedAt !== activeRun.createdAt && ` · Last updated ${new Date(activeRun.updatedAt).toLocaleString()}`}
          </Typography>
        )}
      </Box>

      {!activeRun && !loading ? (
        <Alert severity="info">
          Create a new test run to start marking results. Give it a label like <strong>v1.2.0</strong> or <strong>March-24-release</strong>.
        </Alert>
      ) : activeRun && (
        <>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={3} mb={1} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={1}><PhoneAndroid fontSize="small" color="primary" /><Typography variant="caption" color="text.secondary">Mobile = rider + driver apps</Typography></Box>
              <Box display="flex" alignItems="center" gap={1}><Storage fontSize="small" color="secondary" /><Typography variant="caption" color="text.secondary">Backend = AWS infrastructure</Typography></Box>
              <Box display="flex" alignItems="center" gap={1}><Web fontSize="small" color="info" /><Typography variant="caption" color="text.secondary">Portal = this admin portal</Typography></Box>
              <Box display="flex" alignItems="center" gap={1}><Chip label="All" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} /><Typography variant="caption" color="text.secondary">= all three</Typography></Box>
            </Box>
            <LinearProgress variant="determinate" value={pct}
              color={totalFailed > 0 ? 'error' : readyForProd ? 'success' : 'primary'}
              sx={{ height: 8, borderRadius: 4, mb: 1.5 }} />
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip label={`${totalPassed} passed`} size="small" color="success" />
              {totalFailed > 0 && <Chip label={`${totalFailed} failed`} size="small" color="error" />}
              {totalPending > 0 && <Chip label={`${totalPending} pending`} size="small" variant="outlined" />}
              <Chip label={`${total} total`} size="small" variant="outlined" />
            </Box>
            {readyForProd && <Alert severity="success" sx={{ mt: 2 }}>All {total} tests passed. This build is cleared for production promotion.</Alert>}
            {totalFailed > 0 && <Alert severity="error" sx={{ mt: 2 }}>{totalFailed} test{totalFailed > 1 ? 's' : ''} failed. Do not promote to production until all failures are resolved.</Alert>}
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Click any result chip to cycle: <strong>Pending → Pass → Fail → Skip → Pending</strong>.
              Results are saved to the database instantly — pick up from any machine or device.
            </Typography>
          </Alert>

          {ALL_SECTIONS.map(({ title, tests }) => (
            <TestSection key={title} title={title} tests={tests} results={results} onToggle={toggle} />
          ))}
        </>
      )}

      <Dialog open={showNewRun} onClose={() => setShowNewRun(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New Test Run</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Give this run a label — typically the release version or date.
          </Typography>
          <TextField autoFocus fullWidth size="small"
            label="Label (e.g. v1.2.0 or March-24-release)"
            value={newLabel} onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createRun()} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNewRun(false)}>Cancel</Button>
          <Button variant="contained" onClick={createRun} disabled={!newLabel.trim() || creating}>
            {creating ? <CircularProgress size={16} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
